import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, ClipboardCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const AGES = ["U7","U8","U9","U10","U11","U12","U13","U14","U15","U16","U18","Adults"];
const empty = { first_name: "", last_name: "", email: "", phone: "", fan_number: "", date_of_birth: "", prior_experience: "", age_group: "", notes: "",
  dbs_held: false, dbs_expiry: "", safeguarding_children_held: false, safeguarding_children_expiry: "", intro_first_aid_expiry: "", highest_coaching_qualification: "" };

export function ProposeCoachDialog({ open, onClose, onSubmitted, editing }) {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(editing ? { ...empty, ...Object.fromEntries(Object.entries(editing.payload || {}).map(([k, v]) => [k, v ?? (typeof empty[k] === "boolean" ? false : "")])) } : empty); }, [open, editing?.id]);

  const canSubmit = form.first_name.trim() && form.last_name.trim() && form.email.trim() && form.fan_number.trim() && form.date_of_birth;
  const reset = () => setForm(empty);

  const submit = async () => {
    if (!canSubmit) { toast.error("First name, surname, email, FAN number and date of birth are required"); return; }
    setBusy(true);
    try {
      const body = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        fan_number: form.fan_number.trim(),
        date_of_birth: form.date_of_birth,
        prior_experience: form.prior_experience.trim() || null,
        age_group: form.age_group || null,
        notes: form.notes.trim() || null,
        dbs_held: !!form.dbs_held, dbs_expiry: form.dbs_expiry || null,
        safeguarding_children_held: !!form.safeguarding_children_held, safeguarding_children_expiry: form.safeguarding_children_expiry || null,
        intro_first_aid_expiry: form.intro_first_aid_expiry || null,
        highest_coaching_qualification: form.highest_coaching_qualification.trim() || null,
      };
      if (editing) await api.put(`/proposals/${editing.id}`, body); else await api.post("/proposals/coach", body);
      toast.success(editing ? "Details updated — admins have been notified" : "Coach registration submitted — admins have been notified");
      reset();
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't submit proposal");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose?.(); } }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="propose-coach-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-2xl flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-[#83B68B]" /> {editing ? "Update coach registration" : "Register a new coach"}
          </DialogTitle>
          <DialogDescription className="text-xs opacity-70">
            The Registrations team checks DBS, safeguarding and FA Whole Game set-up, then approves — you'll be updated at each stage. Once approved, we'll create their coach login and email their welcome details.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">First name <span className="text-[#83B68B]">*</span></Label>
              <Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} data-testid="propose-coach-first" />
            </div>
            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">Surname <span className="text-[#83B68B]">*</span></Label>
              <Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} data-testid="propose-coach-last" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] tracking-widest uppercase opacity-70">Email <span className="text-[#83B68B]">*</span></Label>
              <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="coach@example.com" data-testid="propose-coach-email" />
            </div>
            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">Phone</Label>
              <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="07…" data-testid="propose-coach-phone" />
            </div>
            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">FAN number <span className="text-[#83B68B]">*</span></Label>
              <Input value={form.fan_number} onChange={e => setForm({ ...form, fan_number: e.target.value })} placeholder="Existing FA number" data-testid="propose-coach-fan" />
            </div>
            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">Date of birth <span className="text-[#83B68B]">*</span></Label>
              <Input type="date" value={form.date_of_birth} onChange={e => setForm({ ...form, date_of_birth: e.target.value })} data-testid="propose-coach-dob" />
            </div>
            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">Age group they'll coach</Label>
              <Select value={form.age_group || "none"} onValueChange={v => setForm({ ...form, age_group: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="propose-coach-age"><SelectValue placeholder="Pick an age group" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— unsure —</SelectItem>
                  {AGES.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="rounded-md border border-border p-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="propose-coach-compliance">
            <div className="sm:col-span-2 text-[10px] tracking-widest uppercase opacity-60">Compliance (if already known)</div>
            <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={!!form.dbs_held} onChange={e => setForm({ ...form, dbs_held: e.target.checked })} className="accent-[#83B68B]" data-testid="propose-coach-dbs" /> Holds FA DBS</label>
            <div><Label className="text-[10px] tracking-widest uppercase opacity-70">DBS expiry</Label><Input type="date" value={form.dbs_expiry} onChange={e => setForm({ ...form, dbs_expiry: e.target.value })} data-testid="propose-coach-dbs-expiry" /></div>
            <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={!!form.safeguarding_children_held} onChange={e => setForm({ ...form, safeguarding_children_held: e.target.checked })} className="accent-[#83B68B]" data-testid="propose-coach-safeguarding" /> Safeguarding Children done</label>
            <div><Label className="text-[10px] tracking-widest uppercase opacity-70">Safeguarding expiry</Label><Input type="date" value={form.safeguarding_children_expiry} onChange={e => setForm({ ...form, safeguarding_children_expiry: e.target.value })} data-testid="propose-coach-safeguarding-expiry" /></div>
            <div><Label className="text-[10px] tracking-widest uppercase opacity-70">Intro to First Aid expiry</Label><Input type="date" value={form.intro_first_aid_expiry} onChange={e => setForm({ ...form, intro_first_aid_expiry: e.target.value })} data-testid="propose-coach-first-aid" /></div>
            <div><Label className="text-[10px] tracking-widest uppercase opacity-70">Highest coaching qualification</Label><Input value={form.highest_coaching_qualification} onChange={e => setForm({ ...form, highest_coaching_qualification: e.target.value })} placeholder="e.g. FA Level 1 / Intro to Coaching" data-testid="propose-coach-qual" /></div>
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Prior experience <span className="opacity-50">(optional)</span></Label>
            <Textarea rows={3} value={form.prior_experience} onChange={e => setForm({ ...form, prior_experience: e.target.value })} placeholder="Coaching badges, clubs coached at, years involved…" data-testid="propose-coach-experience" />
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Notes for admin</Label>
            <Textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="How you know them, why they're a good fit…" data-testid="propose-coach-notes" />
          </div>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={() => { reset(); onClose?.(); }} className="flex-1 py-3 rounded-md border border-border text-xs tracking-widest uppercase font-display font-bold hover:border-white/40" data-testid="propose-coach-cancel">Not now</button>
            <button type="button" onClick={submit} disabled={!canSubmit || busy} data-testid="propose-coach-submit" className="btn-volt flex-1 py-3 rounded-md disabled:opacity-40 inline-flex items-center justify-center gap-2">
              <ClipboardCheck className="w-4 h-4" /> {busy ? "Sending…" : editing ? "Save changes" : "Send for review"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
